import { SAVED_VIEW, TSavedView } from '../constants';
import { CHANGE_STATUS, TChangeStatus, TChangeV2, TCriticality, TScopeType } from '../models';
import { getChangeReference } from './automationRules';
import { getDayOffset } from './format';

const OPEN_STATUSES: TChangeStatus[] = [
  CHANGE_STATUS.DRAFT,
  CHANGE_STATUS.SUBMITTED,
  CHANGE_STATUS.IN_ASSESSMENT,
  CHANGE_STATUS.INITIAL_PLANNING,
  CHANGE_STATUS.IN_EXECUTION,
  CHANGE_STATUS.IN_COMPLETION,
];

export const isOpenChange = (change: TChangeV2): boolean => OPEN_STATUSES.includes(change.status);

export const isOverdue = (change: TChangeV2): boolean => {
  if (!isOpenChange(change) || !change.targetDate) return false;
  const offset = getDayOffset(change.targetDate);
  return offset !== undefined && offset < 0;
};

export const isBlocked = (change: TChangeV2): boolean =>
  change.blockers.length > 0 || Boolean(change.awaitingExternalDecision);

export const isMyChange = (change: TChangeV2, user: string): boolean =>
  change.initiator === user ||
  change.changeManager === user ||
  change.currentOwner === user ||
  change.collaborators.includes(user);

export type TDashboardSummary = {
  myChanges: number;
  myTasks: number;
  overdue: number;
  blocked: number;
};

export const getDashboardSummary = (changes: TChangeV2[], user: string): TDashboardSummary => ({
  myChanges: changes.filter((change) => isMyChange(change, user) && isOpenChange(change)).length,
  myTasks: changes.reduce(
    (total, change) => total + change.tasks.filter((task) => task.owner === user && task.status !== 'Done').length,
    0
  ),
  overdue: changes.filter(isOverdue).length,
  blocked: changes.filter(isBlocked).length,
});

export type TDashboardFilters = {
  search: string;
  status: TChangeStatus | '';
  scope: TScopeType | '';
  category: string;
  changeManager: string;
  criticality: TCriticality | '';
  myWork: boolean;
  overdueOnly: boolean;
  blockedOnly: boolean;
};

export const EMPTY_DASHBOARD_FILTERS: TDashboardFilters = {
  search: '',
  status: '',
  scope: '',
  category: '',
  changeManager: '',
  criticality: '',
  myWork: false,
  overdueOnly: false,
  blockedOnly: false,
};

export const hasActiveFilters = (filters: TDashboardFilters): boolean =>
  Boolean(
    filters.search ||
      filters.status ||
      filters.scope ||
      filters.category ||
      filters.changeManager ||
      filters.criticality ||
      filters.myWork ||
      filters.overdueOnly ||
      filters.blockedOnly
  );

export const filterChanges = (changes: TChangeV2[], filters: TDashboardFilters, user: string): TChangeV2[] => {
  const normalizedSearch = filters.search.trim().toLocaleLowerCase();
  return changes.filter((change) => {
    if (filters.status && change.status !== filters.status) return false;
    if (filters.scope && change.scopeType !== filters.scope) return false;
    if (filters.category && change.changeCategory !== filters.category) return false;
    if (filters.changeManager && change.changeManager !== filters.changeManager) return false;
    if (filters.criticality && change.criticality !== filters.criticality) return false;
    if (filters.myWork && !isMyChange(change, user)) return false;
    if (filters.overdueOnly && !isOverdue(change)) return false;
    if (filters.blockedOnly && !isBlocked(change)) return false;
    if (!normalizedSearch) return true;
    return [getChangeReference(change), change.title, change.changeManager ?? '']
      .join(' ')
      .toLocaleLowerCase()
      .includes(normalizedSearch);
  });
};

// A saved view is a named preset. It filters the base list before column filters.
export const applySavedView = (changes: TChangeV2[], view: TSavedView, user: string): TChangeV2[] => {
  switch (view) {
    case SAVED_VIEW.MY_ACTIVE:
      return changes.filter((change) => isMyChange(change, user) && isOpenChange(change));
    case SAVED_VIEW.NEEDS_ATTENTION:
      return changes.filter((change) => isBlocked(change) || isOverdue(change));
    case SAVED_VIEW.AWAITING_EXTERNAL:
      return changes.filter((change) => change.awaitingExternalDecision);
    case SAVED_VIEW.ALL:
    default:
      return changes;
  }
};

// The secondary line in the Change cell: the affected product, else the QMS area.
export const getChangeContextLabel = (change: TChangeV2): string => {
  if (change.affectedProducts.length > 0) return change.affectedProducts.join(', ');
  if (change.affectedQmsAreas.length > 0) return change.affectedQmsAreas.join(', ');
  if (change.affectedProductFamilies.length > 0) return change.affectedProductFamilies.join(', ');
  return 'No scope set';
};

// The scope shown under the change title now that the Scope column is gone: the
// affected products and QMS areas together, kept short (the cell truncates it).
export const getChangeScopeSummary = (change: TChangeV2): string => {
  const parts = [...change.affectedProducts, ...change.affectedQmsAreas];
  return parts.length > 0 ? parts.join(', ') : 'No scope set';
};

export const sortChanges = (changes: TChangeV2[]): TChangeV2[] =>
  [...changes].sort((a, b) => {
    const aId = a.displayId ?? `zzz-${a.createdAt}`;
    const bId = b.displayId ?? `zzz-${b.createdAt}`;
    return bId.localeCompare(aId);
  });
